import { FormElementHelper } from '@/public/shared/helpers/form-element-helper';
import { ApiClient } from '@/shared/api-client';
import { IHttpValidationError } from '@/shared/api-client/models';
import { INewNomination } from '@/shared/api-client/models/new-nomination';
import { ArgumentValidator } from '@/shared/helpers';
import { IElection } from '@/shared/models/election';
import { NominationCustomFieldInputType } from '@/shared/models/nomination-custom-field-input-type';
import { NominationFieldType } from '@/shared/models/nomination-field-type';
import { NominationTargetType } from '@/shared/models/nomination-target-type';
import _ from 'lodash';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {IValidationErrorViewModel} from '@/public/shared/models/validation-error-viewModel';

type NominationViewState = 'NominationForm' | 'SuccessMessage' | 'SuccessMessageSelfNomination';


interface IViewModel {

    errors: IValidationErrorViewModel[];

    isStoring: boolean;

    viewState: NominationViewState;
}

interface ICustomField {

    type: NominationFieldType;

    fieldName: string;
}

interface IFormDataModel {

    target: NominationTargetType;

    personalIdentityNumber: string;

    membershipNumber: string;

    firstName: string;

    lastName: string;

    email: string;

    telephoneNumber: string;

    description: string;

    lucatId: string;

    customField1: string;

    customField2: string;

    customField3: string;

    customField4: string;

    customField5: string;

    askedForConsent: boolean;
}

@Component({
    components: {

    }
})
export default class NominationComponent extends Vue {

    @Prop() public election!: IElection;
    @Prop() public translations!: { [key: string]: string; };
    @Prop() public enabledFields!: NominationFieldType[];
    @Prop() public requiredFields!: NominationFieldType[];
    @Prop() public customFieldInputTypes!: { [key: string]: NominationCustomFieldInputType; };
    @Prop() public simpleSelfNominationForm!: boolean;
    @Prop() public confirmationOfConsent!: boolean;
    @Prop() public culture!: string;

    public showNominationTargets = true;

    public viewModel: IViewModel = {
        errors: [],
        isStoring: false,
        viewState: 'NominationForm'
    };

    public customFields: ICustomField[] = [
        { type: 'CustomField1', fieldName: 'customField1'},
        { type: 'CustomField2', fieldName: 'customField2'},
        { type: 'CustomField3', fieldName: 'customField3'},
        { type: 'CustomField4', fieldName: 'customField4'},
        { type: 'CustomField5', fieldName: 'customField5'}
    ];

    public formData: IFormDataModel = {
        target: 'Undefined',
        personalIdentityNumber: '',
        membershipNumber: '',
        telephoneNumber: '',
        description: '',
        lucatId: '',
        askedForConsent: false,
        firstName: '',
        lastName: '',
        email: '',

        customField1: '',
        customField2: '',
        customField3: '',
        customField4: '',
        customField5: ''
    };

    private validateOnChanges = false;

    private apiClient: ApiClient = new ApiClient();

    constructor() {
        super();
    }

    public created(): void {

        this.setup();
    }

    @Watch('formData.description') public onDescriptionChanged(newVal: string, oldVal: string) {

        this.validate(false);
    }

    @Watch('formData.askedForConsent') public onAskedForConsentChanged(newVal: string, oldVal: string) {

        this.validate(false);
    }

    public get enableSimpleSelfNominationForm(): boolean {

        return this.simpleSelfNominationForm && this.election.allowSelfNomination;
    }

    public get enableConfirmationOfConsent(): boolean {
        return this.confirmationOfConsent;
    }

    public get formControlsWithErrors(): string[] {

        if (!this.viewModel.errors || this.viewModel.errors.length <= 0) {
            return [];
        }

        const errorFields = this.viewModel.errors.map(x => x.formControlId || 'noField');

        return _.uniq(errorFields);
    }

    public setup(): void {

        this.viewModel.viewState = 'NominationForm';

        if (!this.enableSimpleSelfNominationForm) {

            this.showNominationTargets = false;

            this.formData.target = 'User';

        } else {

            this.showNominationTargets = true;

            this.formData.target = 'Undefined';
        }
    }

    public async setTarget(target: NominationTargetType) {

        this.validateOnChanges = false;
        this.viewModel.errors = [];

        this.formData = {
            target,
            personalIdentityNumber: '',
            membershipNumber: '',
            telephoneNumber: '',
            description: '',
            lucatId: '',
            askedForConsent: false,
            firstName: '',
            lastName: '',
            email: '',

            customField1: '',
            customField2: '',
            customField3: '',
            customField4: '',
            customField5: ''
        };
    }

    public async submit($event: UIEvent) {

        $event.preventDefault();

        if (this.viewModel.isStoring) {

            return;
        }

        this.validate(true);

        if (this.viewModel.errors !== null && this.viewModel.errors.length >= 1) {
            return;
        }

        this.viewModel.isStoring = true;

        try {

            const postModel: INewNomination = {
                target: this.formData.target,
                personalIdentityNumber: this.formData.personalIdentityNumber,
                membershipNumber: this.formData.membershipNumber,
                firstName: this.formData.firstName,
                lastName: this.formData.lastName,
                telephoneNumber: this.formData.telephoneNumber,
                description: this.formData.description,
                notificationCulture: this.culture,
                email: this.formData.email,
                lucatId: this.formData.lucatId,
                customField1: this.formData.customField1,
                customField2: this.formData.customField2,
                customField3: this.formData.customField3,
                customField4: this.formData.customField4,
                customField5: this.formData.customField5
            };

            await this.apiClient.nomination.create(this.election.electionId.id, postModel);

            this.viewModel.isStoring = false;

            this.nominationSucceeded();

        } catch (error: any) {

            const typedError = error as IHttpValidationError;

            this.viewModel.isStoring = false;

            if (typedError.type === 'ValidationException') {

                const validationErrors = typedError.errors;

                Object.keys(validationErrors).forEach(key => {

                    const messages = validationErrors[key] as string[];

                    messages.forEach( (message) => {

                        const camelCasedMessage = _.camelCase(message);

                        this.viewModel.errors.push({
                            formControlId: key,
                            name: this.translations[`form_${key}_header`] || '',
                            message: this.translations[`validation_error_${camelCasedMessage}`] || '',
                        });
                    });
                });

                return;

            } else if (typedError.type === 'InvalidModelException') {

                // Generic api validation error
                this.viewModel.errors.push({
                    name: this.translations[`validation_error_api_general_header`] || '',
                    message: this.translations[`validation_error_api_general_message`] || ''
                });

                return;
            }

            // Generic error
            this.viewModel.errors.push({
                name: this.translations[`validation_error_general_header`] || '',
                message: this.translations[`validation_error_general_message`] || ''
            });
        }
    }

    public isEnabled(field: NominationFieldType): boolean {

        ArgumentValidator.notNullOrEmpty(field, `field`);

        return this.enabledFields.includes(field);
    }

    public isRequired(field: NominationFieldType): boolean {

        ArgumentValidator.notNullOrEmpty(field, `field`);

        return this.requiredFields.includes(field);
    }

    public customFieldInputType(field: NominationFieldType): NominationCustomFieldInputType {

        ArgumentValidator.notNullOrEmpty(field, `field`);

        if (!this.customFieldInputTypes) {
            return 'SingleLineText';
        }

        if (!this.customFieldInputTypes[field]) {
            return 'SingleLineText';
        }

        return this.customFieldInputTypes[field];
    }

    public handlePersonalIdentityNumberChanged($event: UIEvent): void {

        if (this.formData.personalIdentityNumber === null || this.formData.personalIdentityNumber.length  < 2) {

            this.validate(false);

            return;
        }

        // We only want to remodel if the cursor is at the end of the input field
        const targetElement = ($event.target as HTMLInputElement);

        const cursorPosition = targetElement.selectionStart || 0;

        if (cursorPosition < this.formData.personalIdentityNumber.length) {

            this.validate(false);

            return;
        }

        const onlyDigits = this.formData.personalIdentityNumber.replace(/[^0-9]/g, '');

        const parsedYear = parseInt(onlyDigits.substring(0, 2), 10);

        const currentYear = new Date().getFullYear() % 100;

        if (!isNaN(parsedYear) && parsedYear > currentYear) {
            this.formData.personalIdentityNumber = '19' + onlyDigits;
        }

        if (onlyDigits.length > 8) {

            this.formData.personalIdentityNumber = `${onlyDigits.substring(0, 8)}-${onlyDigits.substring(8)}`;
        }

        this.validate(false);
    }

    public handleTelephoneNumberChanged($event: UIEvent): void {

        this.formData.telephoneNumber = FormElementHelper.tryParseTelephoneNumber(this.formData.telephoneNumber);

        this.validate(false);
    }

    private nominationSucceeded(): void {

        if (this.formData.target === 'Self') {

            this.viewModel.viewState = 'SuccessMessageSelfNomination';

        } else {
            this.viewModel.viewState = 'SuccessMessage';
        }

        this.setTarget('Undefined');
    }

    private validate(forceValidation?: boolean): void {

        if (forceValidation === false && this.validateOnChanges === false) {

            return;
        }

        let isValid = true;
        this.viewModel.errors = [];

        // Validation if nominating other user, no need to validate self-nomination
        if (this.formData.target === 'User') {

            if (!this.validateIdentificationInformation(this.formData)) {

                isValid = false;

                this.viewModel.errors.push({

                    name: this.translations[`validation_error_general_header`],
                    message: this.translations[`validation_error_missingIdentificationInformation`]
                });
            }

            if (this.isEnabled('PersonalIdentityNumber')) {

                if (this.isRequired('PersonalIdentityNumber') && !this.formData.personalIdentityNumber) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'personalIdentityNumber',
                        name: this.translations[`form_personalIdentityNumber_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }

                if (!FormElementHelper.validatePersonalIdentifierNumber(this.formData.personalIdentityNumber)) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'personalIdentityNumber',
                        name: this.translations[`form_personalIdentityNumber_header`],
                        message: this.translations[`validation_error_personalIdentityNumber_inValid`]
                    });
                }
            }

            if (this.isEnabled('MembershipNumber')) {

                if (this.isRequired('MembershipNumber') && !this.formData.membershipNumber) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'membershipNumber',
                        name: this.translations[`form_membershipNumber_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }
            }

            if (this.isEnabled('FirstName')) {

                if (this.isRequired('FirstName') && !this.formData.firstName) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'firstName',
                        name: this.translations[`form_firstName_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }
            }

            if (this.isEnabled('LastName')) {

                if (this.isRequired('LastName') && !this.formData.lastName) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'lastName',
                        name: this.translations[`form_lastName_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }
            }

            if (this.isEnabled('Telephone')) {

                if (this.isRequired('Telephone') && !this.formData.telephoneNumber) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'telephoneNumber',
                        name: this.translations[`form_telephoneNumber_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }

                if (!FormElementHelper.validateTelephoneNumber(this.formData.telephoneNumber)) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'telephoneNumber',
                        name: this.translations[`form_telephoneNumber_header`],
                        message: this.translations[`validation_error_telephoneNumber_inValid`]
                    });
                }
            }

            if (this.isEnabled('Email')) {

                if (this.isRequired('Email') && !this.formData.email) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'email',
                        name: this.translations[`form_email_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }

                if (!FormElementHelper.validateEmail(this.formData.email)) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'email',
                        name: this.translations[`form_email_header`],
                        message: this.translations[`validation_error_email_inValid`]
                    });
                }
            }

            if (this.isEnabled('LucatId')) {

                if (this.isRequired('LucatId') && !this.formData.lucatId) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'lucatId',
                        name: this.translations[`form_lucatId_header`],
                        message: this.translations[`validation_error_generic_required`]
                    });
                }

                // Lucat-ID must be an valid email address
                if (!FormElementHelper.validateEmail(this.formData.lucatId)) {

                    isValid = false;

                    this.viewModel.errors.push({

                        formControlId: 'lucatId',
                        name: this.translations[`form_lucatId_header`],
                        message: this.translations[`validation_error_lucatId_inValid`]
                    });
                }
            }

            this.customFields.forEach(x => {

                if (this.isEnabled(x.type)) {

                    const dataSource = _.get(this.formData, x.fieldName);

                    if (this.isRequired(x.type) && !dataSource) {

                        isValid = false;

                        this.viewModel.errors.push({
                            formControlId: x.fieldName,
                            name: this.translations[`form_${x.fieldName}_header`],
                            message: this.translations[`validation_error_generic_required`]
                        });
                    }
                }
            });

            if (!this.validateAskedForConsent(this.formData.askedForConsent)) {

                isValid = false;

                this.viewModel.errors.push({

                    formControlId: 'askedForConsent',
                    name: this.translations[`form_askedForConsent_label`],
                    message: this.translations[`validation_error_needConsent`]
                });
            }
        }

        if (!isValid && this.validateOnChanges === false) {

            this.validateOnChanges = true;
        }
    }

    private validateAskedForConsent(askedForConsent: boolean): boolean {

        if (this.confirmationOfConsent === false) {

            // No validation
            return true;
        }

        if (askedForConsent === null) {

            return false;
        }

        return askedForConsent;
    }

    private validateIdentificationInformation(formData: IFormDataModel): boolean {

        return formData.personalIdentityNumber !== '' ||
               formData.membershipNumber !== '' ||
               formData.telephoneNumber !== '' ||
               formData.description !== '' ||
               formData.lucatId !== '' ||
               formData.firstName !== '' ||
               formData.lastName !== '' ||
               formData.email !== '' ||
               formData.customField1 !== '' ||
               formData.customField2 !== '' ||
               formData.customField3 !== '' ||
               formData.customField4 !== '' ||
               formData.customField5 !== '';
    }

}
